<template>
    <div>
        <section class="view-employee account-section" v-if="loaded">
            <div class="account-container">

                <div class="mb-5 d-flex flex-column flex-lg-row align-items-lg-center justify-content-lg-between">
                    <div class="d-flex align-items-center">
                        <div class="employee-circle mr-4">
                            {{ getCircleInitials() }}
                        </div>
                        <div>
                            <h1 class="h2 m-0">
                                <template v-if="employee.full_name && !organization.old_mode_no_employee_sigs">
                                    {{ employee.full_name }}
                                </template>
                                <template v-else>
                                    <a v-bind:href="'mailto:' + employee.email" class="email-link">{{ employee.email }}</a>
                                </template>
                            </h1>
                            <div class="mt-1" v-if="employee.full_name === null && employee_usage_count >= 3">
                                <span class="badge badge-pill badge-warning">
                                    Email changed {{ employee_usage_count }} times!
                                </span>
                            </div>
                            <div class="mt-1" v-if="employee.title || employee.full_name">
                                <div v-if="employee.full_name">
                                    <a v-bind:href="'mailto:' + employee.email" class="email-link" v-if="!organization.old_mode_no_employee_sigs">{{ employee.email }}</a>
                                    <span class="badge badge-pill badge-warning" v-if="employee_usage_count >= 3">
                                        Changed {{ employee_usage_count }} times!
                                    </span>
                                </div>
                                <div v-if="employee.title && !organization.old_mode_no_employee_sigs">
                                    {{ employee.title }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="ml-lg-3 mt-3 mt-lg-0">
                        <a class="btn btn-primary" href="/employees">View Employees</a>
                    </div>
                </div>

                <div class="signature-list">
                    <div class="mb-4 d-flex align-items-center justify-content-between">
                        <h4 class="m-0 color-light-bluegray">
                            {{ employee.employee_signatures.length }} Employee <template v-if="employee.employee_signatures.length === 1">Signature</template><template v-else>Signatures</template>
                        </h4>
                    </div>
                    <div class="card signature-card mb-3" v-for="employee_signature in employee.employee_signatures">
                        <div class="card-body">
                            <div class="d-flex align-items-center justify-content-between">
                                <div class="title-details">
                                    <h4 class="signature-title">
                                        <template v-if="employee_signature.signature">
                                            {{ employee_signature.signature.name }}
                                        </template>
                                        <template v-else>
                                            Signature Template Deleted
                                        </template>
                                    </h4>
                                    <div class="signature-meta">
                                        Created {{ employee_signature.created_at | toDate }}
                                        <template v-if="employee_signature.created_at_formatted !== employee_signature.updated_at_formatted">
                                            &nbsp;/&nbsp; Updated {{ employee_signature.updated_at | toDate }}
                                        </template>
                                        &nbsp;/&nbsp; Used {{ employee_signature.uses }}x
                                        <template v-if="employee_signature.old_mode">
                                            &nbsp;/&nbsp; <a class="badge badge-pill badge-light-bluegray" href="/faq#faq-21" target="_blank">Only Email Saved</a>
                                        </template>
                                    </div>
                                </div>
                                <div>
                                    <button class="btn btn-sm btn-mediumblue" v-bind:class="{ 'btn-disabled': !hasGeneratorLink(employee_signature)}" v-on:click.prevent="openEmployeeSigLinkModal(employee_signature)" v-if="!organization.old_mode_no_employee_sigs && employee_signature.signature">Share</button>
                                    <a v-bind:href="'/employees/' + employee.id + '/signature/' + employee_signature.id" class="btn btn-mediumblue btn-sm" v-if="!organization.old_mode_no_employee_sigs && employee_signature.signature">Edit</a>
                                    <button class="btn btn-mediumblue btn-sm" v-on:click.prevent="deleteEmployeeSignature(employee_signature.id, employee_signature.signature_id)">Delete</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="card signature-card mb-3 px-4 py-2 color-light-bluegray text-small" v-if="employee.employee_signatures.length === 0">
                        Their employee signature may have been deleted, or a template may have been deleted that included their employee signature.
                    </div>
                    <div class="mt-4" v-if="hasOtherTemplates()">
                        <button class="add-new btn btn-primary" v-on:click.prevent="openTemplatesModal()">New Employee Signature</button>
                    </div>
                    <div class="mt-5">
                        <a href="#" class="btn btn-danger-subtle btn-sm" v-on:click.prevent="deleteEmployee()">Delete Employee</a>
                    </div>
                </div>

                <div id="templates-modal" class="templates-modal modal ui-light-theme" v-bind:class="{ show: showTemplatesModal }" tabindex="-1" role="dialog" aria-modal="true" v-if="showTemplatesModal" v-on:click.self.prevent="showTemplatesModal = false">
                    <div class="modal-dialog" role="document">
                        <div class="modal-content">
                            <div class="modal-header">
                                <h5 class="modal-title">Select A Template</h5>
                                <button type="button" class="close" v-on:click.prevent="showTemplatesModal = false" aria-label="Close">
                                    <span aria-hidden="true">×</span>
                                </button>
                            </div>
                            <div class="modal-body">
                                <p>Please select a template for this new employee signature.</p>
                                <a v-bind:href="templateLink(template)" v-for="template in templates" v-bind:key="template.id" class="card signature-card signature-card-select">
                                    <div class="card-body">
                                        <div class="d-flex align-items-center justify-content-between">
                                            <div class="d-flex align-items-center">
                                                <div class="status" v-if="!organization.old_mode_no_employee_sigs">
                                                    <div class="status-circle" v-bind:class="{ published: template.employee_signatures.length >= 1, draft: template.employee_signatures.length === 0 }"></div>
                                                    <div class="status-name">
                                                        <template v-if="template.employee_signatures.length >= 1">
                                                            Created
                                                        </template>
                                                        <template v-else>
                                                            Unused
                                                        </template>
                                                    </div>
                                                </div>
                                                <div class="title-details">
                                                    <h4 class="signature-title">{{ template.name }}</h4>
                                                    <div class="signature-meta">{{ template.created_at_formatted }}</div>
                                                </div>
                                            </div>
                                            <div class="pl-3">
                                                <div class="btn btn-mediumblue btn-sm" v-if="template.employee_signatures.length >= 1 &&  !organization.old_mode_no_employee_sigs">View<span class="d-none d-lg-inline"> & Edit</span></div>
                                                <div class="btn btn-mediumblue btn-sm" v-else>Create</div>
                                            </div>
                                        </div>  
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                </div><!-- .templates-modal -->

                <div id="employee-sig-link-modal" class="employee-sig-link-modal modal ui-light-theme" v-bind:class="{ show: showEmployeeSigLinkModal }" tabindex="-1" role="dialog" aria-modal="true" v-if="showEmployeeSigLinkModal" v-on:click.self.prevent="showEmployeeSigLinkModal = false">
                    <div class="modal-dialog" role="document">
                        <div class="modal-content">
                            <div class="modal-header">
                                <h5 class="modal-title">Private Employee Signature Link</h5>
                                <button type="button" class="close" v-on:click.prevent="showEmployeeSigLinkModal = false" aria-label="Close">
                                    <span aria-hidden="true">×</span>
                                </button>
                            </div>
                            <div class="modal-body">
                                <template v-if="employeeSigLink">
                                    <p>This is the private link that {{ employee.full_name }} can visit to copy or modify their signature:</p>
                                    <p class="position-relative"><input type="text" name="share-slug" class="form-control form-control-lg" v-model="employeeSigLink"><button class="btn btn-mediumblue btn-sm copy-button" v-on:click.prevent="copyEmployeeSigLink()">
                                        <template v-if="employeeSigLinkCopied">
                                            Copied!
                                        </template>
                                        <template v-else>
                                            Copy
                                        </template>
                                    </button></p>
                                </template>
                                <template v-else>
                                    <p class="m-0">It looks like this signature doesn't have a published generator link. This is required for an employee to be able to view or edit their signature. If you would like to be able to share employee signatures, please return to the signature, visit the <strong>Settings</strong> item near the top right of the screen and <strong>Publish</strong> your generator.</p>
                                </template>
                            </div>
                        </div>
                    </div>
                </div><!-- #employee-sig-link-modal -->

            </div>
        </section>
        
    </div>
</template>

<script>
    import { DateTime } from "luxon";
    import * as clipboard from "clipboard-polyfill";
    export default {
        props: {
            load_employee_id: {
                type: String,
                default: null,
            },
            load_env: {
                type: String,
                default: 'production',
            }
        },
        data: function () {
            return {
                user: null,
                organization: null,
                employee_usage_count: null,
                employee: null,
                templates: [],
                loaded: false,
                showTemplatesModal: false,
                employeeSigLink: '',
                employeeSigLinkModalSig: null,
                employeeSigLinkCopied: false,
                showEmployeeSigLinkModal: false,
            }
        },
        mounted() {
            var self = this;
            axios.get('/employees/' + this.load_employee_id + '/data')
            .then(function(response) {
                self.user = response.data.user;
                self.organization = response.data.organization;
                self.employee_usage_count = response.data.employee_usage_count;
                self.employee = response.data.employee;
                self.templates = response.data.templates;
                self.loaded = true;
            }.bind(this)).catch(function(data) {
                console.log('error');
            });
        },
        computed: {
            additionalPurchaseButtonText: function () {
                if (this.additionalPurchaseLoading) {
                    return "Please wait... <img src='/img/spinner-white.gif' width='30' height='30'>";
                } else {
                    var additionalTotal = this.additionalSigs * this.solo_plan_price;
                    if (this.additionalSigs === 1) {
                        return "Purchase " + this.additionalSigs + " Signature for $" + additionalTotal;
                    } else {
                        return "Purchase " + this.additionalSigs + " Signatures for $" + additionalTotal;
                    }
                }
            },
            employeesAvailable: function() {
                if (this.organization.plan_name && this.organization.plan_name !== '') {
                    if (this.organization.templates === 99999) {
                        return "Unlimited employees available";
                    } else {
                        var available = this.organization.allowed_employees - this.employees.length;
                        return available + " employees available";
                    }
                } else {
                    return "No employees available";
                }
            }
        },
        filters: {
            toDate: function(date) {
                return DateTime.fromISO(date).toLocaleString();
            },
            toLowerCase: function(string) {
                return string.toLowerCase().trim();
            }
        },
        methods: {
            hasOtherTemplates: function() {
                var availableNewTemplates = 0;
                this.templates.forEach(function (template) {
                    if (template.employee_signatures.length === 0) {
                        availableNewTemplates++;
                    }
                }, availableNewTemplates);
                if (availableNewTemplates === 0) {
                    return false;
                } else {
                    return true;
                }
            },
            hasGeneratorLink: function(employee_signature) {
                if (employee_signature.signature && employee_signature.signature.status == 'Published' && employee_signature.signature.slug) {
                    return true;
                } else {
                    return false;
                }
            },
            openEmployeeSigLinkModal: function(employee_signature) {
                this.showEmployeeSigLinkModal = true;
                this.employeeSigLinkCopied = false;
                this.employeeSigLinkModalSig = employee_signature;
                if (employee_signature.signature && employee_signature.signature.status == 'Published' && employee_signature.signature.slug) {
                    if (this.load_env == 'development') {
                        this.employeeSigLink = 'http://' + employee_signature.signature.slug + '.signatureemail.lndo.site/signature/' + employee_signature.slug;
                    } else {
                        this.employeeSigLink = 'https://' + employee_signature.signature.slug + '.signature.email/signature/' + employee_signature.slug;
                    }
                } else {
                    this.employeeSigLink = null;
                }
            },
            copyEmployeeSigLink: function() {
                var dt = new clipboard.DT();
                dt.setData("text/plain", this.employeeSigLink);
                clipboard.write(dt);
                this.employeeSigLinkCopied = true;
            },
            templateLink: function(template) {
                if (template.employee_signatures.length >= 1 && !this.organization.old_mode_no_employee_sigs) {
                    return '/employees/' + this.employee.id + '/signature/' + template.employee_signatures[0].id;
                } else {
                    return '/employees/' + this.employee.id + '/new/' + template.id;
                }
            },
            openTemplatesModal: function() {
                if (this.templates.length === 1) {
                    window.location.href = this.templateLink(this.templates[0]);
                } else {
                    this.showTemplatesModal = true;
                }
            },
            getCircleInitials: function() {
                if (this.employee && this.employee.full_name) {
                    var names = this.employee.full_name.split(' ');
                    var initials = names[0].substring(0, 1).toUpperCase();
                    if (names.length > 1) {
                        initials += names[names.length - 1].substring(0, 1).toUpperCase();
                    }
                    return initials;
                } else if (this.employee && this.employee.email) {
                    return this.employee.email.substring(0, 1).toUpperCase();
                }
            },
            deleteEmployee: function() {
                var confirmation = confirm("Are you sure you want to delete this employee and all of their signature data permanently? They will no longer be able to recall and edit their signature from the email link we sent them earlier!");
                if (confirmation == true) {
                    var self = this;
                    axios.post('/employees/' + this.load_employee_id + '/delete')
                    .then(function(response) {
                        if (response.data.status == 'success') {
                            window.location.href = "/employees";
                        }
                    }.bind(this)).catch(function(data) {
                        console.log('error');
                    });
                }
            },
            deleteEmployeeSignature: function(employee_signature_id, signature_id) {
                var confirmation = confirm("Are you sure you want to delete this employee signature permanently?");
                if (confirmation == true) {
                    var self = this;
                    axios.post('/employees/' + this.load_employee_id + '/signature/' + employee_signature_id + '/delete')
                    .then(function(response) {
                        if (response.data.status == 'success') {

                            var templateIndex = self.templates.findIndex(function(thetemplate) {
                                return thetemplate.id === signature_id;
                            }, signature_id);
                            if (templateIndex !== -1) {
                                var templateEmployeeSigIndex = self.templates[templateIndex].employee_signatures.findIndex(function(theemployeesig) {
                                    return theemployeesig.id === employee_signature_id;
                                }, employee_signature_id);
                                if (templateEmployeeSigIndex !== -1) {
                                    self.templates[templateIndex].employee_signatures.splice(templateEmployeeSigIndex, 1);
                                }
                            }

                            var employeeSigIndex = self.employee.employee_signatures.findIndex(function(theemployeesig) {
                                return theemployeesig.id === employee_signature_id;
                            }, employee_signature_id);
                            if (employeeSigIndex !== -1) {
                                self.employee.employee_signatures.splice(employeeSigIndex, 1);
                                // Vue.notify({
                                //     group: 'bottomleftmessages',
                                //     title: 'Employee signature deleted!',
                                //     type: 'success',
                                // });
                            }
                        }
                    }.bind(this)).catch(function(data) {
                        console.log('error');
                    });
                }
            }
        }
    }
</script>
